import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, api, toastError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFilters } from "@/hooks/useFilters";
import { useStuck } from "@/hooks/useStuck";
import { useTopbar } from "@/lib/topbar";
import { BackLink, Lens } from "@/components/common/Page";
import { LoadingState, ErrorState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { CompanyMark } from "@/components/common/CompanyMark";
import { DocumentsPanel } from "@/components/common/DocumentsPanel";
import { AuditTrail } from "@/components/common/ActivityFeed";
import { UserSelect } from "@/components/common/Pickers";
import { NowPanel } from "@/components/project/NowPanel";
import { Subnav } from "@/components/project/ProjectSubnav";
import { QuoteHeader } from "@/components/quotes/QuoteHeader";
import { QuoteFlow } from "@/components/quotes/QuoteFlow";
import { LinesEditor } from "@/components/quotes/LinesEditor";
import { WebQuoteSheet } from "@/components/quotes/WebQuoteSheet";
import { deriveQuoteLens } from "@/components/quotes/quoteLens";
import { nok, fmtDate, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { FileDown, Loader2, FolderKanban, Globe, ExternalLink, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/**
 * Tilbudsside 2.0 – header (one primary action) → deterministic brief + facts → løpet (lifecycle line) → «Nå» →
 * sticky sub-nav → content. Every sentence/number comes from quoteLens.js; nothing here is guessed.
 */
export default function QuoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useAuth();
  const key = ["quote", id];
  const { data: q, isLoading, error, refetch } = useQuery({ queryKey: key, queryFn: () => get(`/quotes/${id}`) });
  const [version, setVersion] = useState(null);
  const { filters, setFilter } = useFilters(`quote-${id}`, { tab: "innhold" });
  const tab = filters.tab || "innhold";
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [webOpen, setWebOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const invalidate = () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ["quotes"] }); qc.invalidateQueries({ queryKey: ["lead"] }); };

  // sticky sub-nav: `stuck` ⇒ header scrolled out ⇒ condensed identity in the topbar
  const [anchorRef, stuck, anchorNode] = useStuck(48);
  const scrollToTabs = (behavior = "auto") => requestAnimationFrame(() => anchorNode.current?.scrollIntoView({ behavior, block: "start" }));
  const setTab = (t) => { setFilter("tab", t); if (stuck) scrollToTabs("auto"); };

  useEffect(() => { if (q && (version === null || !q.versions.some((v) => v.version === version))) setVersion(q.current_version); }, [q]);

  const send = useMutation({ mutationFn: (note) => post(`/quotes/${id}/send`, { note }), onSuccess: () => { toast.success("Tilbud markert som sendt"); invalidate(); setSendOpen(false); }, onError: (e) => toastError(e) });
  const reject = useMutation({ mutationFn: (body) => post(`/quotes/${id}/reject`, body), onSuccess: () => { toast.success("Tilbud markert som avslått"); invalidate(); setRejectOpen(false); }, onError: (e) => toastError(e) });
  const newVersion = useMutation({ mutationFn: (body) => post(`/quotes/${id}/versions`, body), onSuccess: (v) => { toast.success(`Versjon ${v.version} opprettet`); invalidate(); setVersion(v.version); setFilter("tab", "innhold"); }, onError: (e) => toastError(e) });

  const openPdf = async (vid, download = false) => {
    setPdfBusy(true);
    try {
      const r = await api.get(`/quote-versions/${vid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      if (download) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${q.quote_number}_v${version}.pdf`;
        a.click();
      } else window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toastError(e, "Kunne ikke generere PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  const v = q ? q.versions.find((x) => x.version === version) || q.versions[0] : null;
  const lens = useMemo(() => (q && v ? deriveQuoteLens(q, v, { canSeeCost: can("calculations:read") }) : null), [q, v, can]);
  const identity = useMemo(() => (q ? <QuoteCondensedIdentity q={q} /> : null), [q]);
  useTopbar(identity, Boolean(q) && stuck);

  if (isLoading) return <LoadingState className="py-32" />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  const isCurrent = v?.id === q.current_version_id;
  const locked = !v || ["sendt", "akseptert"].includes(v.status) || q.status === "akseptert";
  const canEdit = can("quotes:write") && !locked && isCurrent;
  const activeShares = (q.shares || []).filter((x) => x.state === "active");

  /* every action on the page goes through here – header primary/menu, «Nå»-panel and the flow line */
  const act = (key) => {
    switch (key) {
      case "send": return setSendOpen(true);
      case "accept": return setAcceptOpen(true);
      case "reject": return setRejectOpen(true);
      case "web": return setWebOpen(true);
      case "pdf": return v && openPdf(v.id);
      case "pdf-download": return v && openPdf(v.id, true);
      case "new-version": return newVersion.mutate({});
      case "new-version-calc": return newVersion.mutate({ from_calculation: true });
      case "project": return q.project && navigate(`/prosjekter/${q.project.id}`);
      case "innhold": return setTab("innhold");
      default: return undefined;
    }
  };
  const go = (item) => { if (item.link) navigate(item.link); else if (item.action) act(item.action); else if (item.tab) setTab(item.tab); };

  const tabs = [{ key: "nav", label: "Tilbud", tabs: [
    { key: "innhold", label: "Innhold og priser", testId: "quote-tab-innhold" },
    { key: "koblinger", label: "Koblinger", testId: "quote-tab-koblinger" },
    { key: "dokumenter", label: "Dokumenter", count: q.document_count || 0, testId: "quote-tab-dokumenter" },
    { key: "historikk", label: "Historikk", count: (q.responses || []).length, testId: "quote-tab-historikk" },
  ] }];

  const versionSwitcher = q.versions.length > 1 && (
    <div className="inline-flex h-8 items-center rounded-md border border-border/70 bg-muted/40 p-0.5" role="tablist" aria-label="Versjoner" data-testid="quote-version-tabs">
      {q.versions.slice().sort((a, b) => a.version - b.version).map((x) => {
        const sel = x.version === version;
        return (
          <button key={x.id} type="button" role="tab" aria-selected={sel} onClick={() => setVersion(x.version)} data-testid={`quote-version-tab-${x.version}`}
            className={cn("inline-flex h-7 items-center gap-1.5 rounded px-2.5 text-[12.5px] transition-colors duration-150", sel ? "bg-card font-medium shadow-[0_0_0_1px_hsl(var(--border))]" : "text-muted-foreground hover:text-foreground")}>
            v{x.version}
            <StatusBadge status={x.status} entity="version" className="text-[11px]" />
            {x.id === q.current_version_id && <span className="text-[11px] text-muted-foreground">gjeldende</span>}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="page-pad space-y-6 fade-up" data-testid="quote-detail-page">
      <BackLink to="/tilbud" testId="quote-back-link">Tilbud</BackLink>
      <QuoteHeader q={q} v={v} lens={lens} can={can} busy={{ pdf: pdfBusy }} onAct={act} />

      <div className="space-y-5">
        <div data-testid="quote-brief">
          <p className="max-w-[80ch] text-[15px] leading-6 text-foreground">{lens.brief[0]} <span className="text-foreground/75">{lens.brief[1]}</span></p>
          <dl className="mt-3 flex flex-wrap gap-x-7 gap-y-2" data-testid="quote-facts-row">
            {lens.facts.map((f) => (
              <div key={f.key} data-testid={`quote-fact-${f.key}`}>
                <dd className={cn("text-[13.5px] font-semibold leading-5", f.mono && "font-mono tabular", f.tone === "warning" && "text-amber-700 dark:text-amber-300", f.tone === "danger" && "text-rose-700 dark:text-rose-300")}>{f.value}</dd>
                <dt className="text-[11.5px] leading-4 text-muted-foreground">{f.label}</dt>
              </div>
            ))}
          </dl>
        </div>
        <QuoteFlow steps={lens.steps} onStep={(s) => s.action && act(s.action)} />
        <NowPanel now={lens.now} onGo={go} testId="quote-now-panel" compact />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <Subnav idPrefix="quote" ariaLabel="Tilbudsfaner" groups={tabs} value={tab} stuck={stuck} anchorRef={anchorRef} onSelect={setTab} onLink={(href) => navigate(href)} />

        <TabsContent value="innhold" className="mt-6">
          {v && <LinesEditor key={v.id} quote={q} v={v} canEdit={canEdit} onSaved={invalidate} versionSwitcher={versionSwitcher} />}
        </TabsContent>

        <TabsContent value="koblinger" className="mt-6">
          <dl className="grid grid-cols-1 gap-x-10 divide-y divide-border/60 text-[13px] md:grid-cols-2 md:divide-y-0 [&>div]:py-3" data-testid="quote-links">
            <div className="md:border-b md:border-border/60">
              <dt className="text-[11.5px] text-muted-foreground">Kunde</dt>
              <dd className="mt-1 flex items-center gap-2.5">
                <CompanyMark kind="customer" id={q.customer_id} src={q.customer?.logo_url || undefined} name={q.customer_name} size="sm" />
                <Link className="font-medium underline-offset-[3px] hover:underline" to={`/kunder/${q.customer_id}`}>{q.customer_name}</Link>
                {q.customer?.org_number && <span className="font-mono text-[12px] text-muted-foreground">{q.customer.org_number}</span>}
                {q.customer?.discount_pct ? <span className="text-[12px] text-muted-foreground">· avtalt rabatt {q.customer.discount_pct} %</span> : null}
              </dd>
            </div>
            <div className="md:border-b md:border-border/60">
              <dt className="text-[11.5px] text-muted-foreground">Henvendelse</dt>
              <dd className="mt-1">{q.lead ? <span className="inline-flex flex-wrap items-center gap-2"><Link className="font-medium underline-offset-[3px] hover:underline" to={`/henvendelser/${q.lead.id}`}>{q.lead.lead_number} – {q.lead.title}</Link><StatusBadge status={q.lead.stage} entity="lead" /></span> : <span className="text-muted-foreground">Ikke knyttet til en henvendelse.</span>}</dd>
            </div>
            <div>
              <dt className="text-[11.5px] text-muted-foreground">Prosjekt</dt>
              <dd className="mt-1">{q.project ? <span className="inline-flex flex-wrap items-center gap-2"><Link className="font-medium underline-offset-[3px] hover:underline" to={`/prosjekter/${q.project.id}`}>{q.project.project_number} – {q.project.name}</Link><StatusBadge status={q.project.status} entity="project" /></span> : <span className="text-muted-foreground">{q.status === "akseptert" ? "Ikke opprettet ennå." : "Opprettes når tilbudet aksepteres."}</span>}</dd>
            </div>
            <div>
              <dt className="text-[11.5px] text-muted-foreground">Kalkyle</dt>
              <dd className="mt-1">{q.calculation ? <span className="inline-flex flex-wrap items-center gap-2"><span className="font-mono text-[12px]">{q.calculation.calc_number}</span><span className="font-medium">{q.calculation.name}</span><StatusBadge status={q.calculation.status} entity="calc" /></span> : <span className="text-muted-foreground">Ingen kalkyle – prislinjene er lagt inn manuelt.</span>}</dd>
            </div>
            <div className="md:col-span-2">
              <dt className="flex items-center justify-between text-[11.5px] text-muted-foreground">Web-tilbud <Button size="sm" variant="outline" className="h-7" onClick={() => setWebOpen(true)} data-testid="quote-web-links-button"><Globe /> Administrer</Button></dt>
              <dd className="mt-1">
                {activeShares.length ? (
                  <ul className="space-y-1" data-testid="quote-web-links">
                    {activeShares.map((x) => (
                      <li key={x.id} className="flex flex-wrap items-center justify-between gap-2">
                        <a href={`${window.location.origin}${x.path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium underline-offset-[3px] hover:underline">Versjon {x.version} <ExternalLink className="h-3 w-3 text-muted-foreground" /></a>
                        <span className="text-[12px] text-muted-foreground">{x.views || 0} {x.views === 1 ? "visning" : "visninger"}{x.last_viewed_at ? ` · sist ${fmtDate(x.last_viewed_at, "d. MMM")}` : ""}{x.expires_at ? ` · gyldig til ${fmtDate(x.expires_at)}` : ""}</span>
                      </li>
                    ))}
                  </ul>
                ) : <span className="text-muted-foreground">Ingen aktiv delingslenke – del tilbudet så kunden kan lese, laste ned PDF og akseptere i nettleseren.</span>}
              </dd>
            </div>
          </dl>
        </TabsContent>

        <TabsContent value="dokumenter" className="mt-6">
          <Lens title="Dokumenter" description="Signerte tilbud, vedlegg og kundekorrespondanse."><DocumentsPanel entityType="quote" entityId={id} defaultCategory="tilbud" /></Lens>
        </TabsContent>

        <TabsContent value="historikk" className="mt-6 grid grid-cols-12 gap-x-8 gap-y-8">
          <div className="col-span-12 space-y-8 xl:col-span-7">
            <Lens title="Versjoner" description="Hver versjon er uforanderlig etter sending – ny versjon lages som kopi.">
              <ul className="divide-y divide-border/60 text-[13px]" data-testid="quote-versions-list">
                {q.versions.map((x) => (
                  <li key={x.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5">
                    <button type="button" className={cn("font-mono text-[12.5px] underline-offset-[3px] hover:underline", x.version === version && "font-semibold")} onClick={() => { setVersion(x.version); setTab("innhold"); }}>v{x.version}</button>
                    <StatusBadge status={x.status} entity="version" />
                    <span className="min-w-0 flex-1 truncate">{x.title}</span>
                    <span className="font-mono tabular">{nok(x.totals?.revenue)}</span>
                    <span className="w-full text-[12px] text-muted-foreground sm:w-auto">{x.created_by_name} · {fmtDateTime(x.created_at)}{x.sent_at ? ` · sendt ${fmtDateTime(x.sent_at)}` : ""}</span>
                  </li>
                ))}
              </ul>
            </Lens>
            <Lens title="Svar fra kunden" description="Registrert via web-tilbudet.">
              {(q.responses || []).length ? (
                <ul className="divide-y divide-border/60 text-[13px]" data-testid="quote-responses-list">
                  {(q.responses || []).map((r) => (
                    <li key={r.id} className="py-2.5">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className={cn("font-medium", r.type === "accept" ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300")}>{r.type === "accept" ? "Akseptert" : "Ba om endringer"}</span>
                        <span className="text-muted-foreground">v{r.version} · {fmtDateTime(r.created_at)}</span>
                        {r.name && <span className="text-muted-foreground">· {r.name}{r.title ? `, ${r.title}` : ""}</span>}
                      </div>
                      {r.message && <p className="mt-1 max-w-[70ch] whitespace-pre-wrap text-[13px] leading-5 text-foreground/85">«{r.message}»</p>}
                    </li>
                  ))}
                </ul>
              ) : <p className="text-[13px] text-muted-foreground">Ingen svar registrert via web-tilbudet.</p>}
            </Lens>
          </div>
          <div className="col-span-12 xl:col-span-5">
            <Lens title="Endringslogg"><AuditTrail entityType="quote" entityId={id} /></Lens>
          </div>
        </TabsContent>
      </Tabs>

      <WebQuoteSheet open={webOpen} onOpenChange={setWebOpen} quote={q} canWrite={can("quotes:write")} onChanged={invalidate} />

      <Sheet open={sendOpen} onOpenChange={setSendOpen}>
        <SheetContent className="w-[440px] max-w-[94vw]" data-testid="quote-send-sheet">
          <SheetHeader><SheetTitle>Marker tilbud som sendt</SheetTitle><SheetDescription>Versjon {v?.version} låses, kalkylen låses og henvendelsen flyttes til «Tilbud sendt». Last ned PDF og send til kunden på e-post – eller del web-tilbudet.</SheetDescription></SheetHeader>
          <SendForm busy={send.isPending} onSend={(note) => send.mutate(note)} onPdf={() => openPdf(v.id, true)} />
        </SheetContent>
      </Sheet>
      <AcceptSheet open={acceptOpen} onOpenChange={setAcceptOpen} quote={q} onDone={(res) => { invalidate(); if (res.created_project) navigate(`/prosjekter/${res.created_project.id}`); }} />
      <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <AlertDialogContent data-testid="quote-reject-dialog">
          <AlertDialogHeader><AlertDialogTitle>Marker tilbud som avslått</AlertDialogTitle><AlertDialogDescription>Oppgi årsak. Du kan også markere henvendelsen som tapt.</AlertDialogDescription></AlertDialogHeader>
          <RejectForm busy={reject.isPending} onReject={(b) => reject.mutate(b)} hasLead={!!q.lead_id} />
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** Condensed identity in the topbar once the header has scrolled away. */
const QuoteCondensedIdentity = ({ q }) => (
  <div className="flex min-w-0 items-center gap-1.5 text-[13px] fade-up" data-testid="quote-topbar-identity">
    <Link to="/tilbud" className="hidden shrink-0 text-muted-foreground transition-colors duration-150 hover:text-foreground sm:inline">Tilbud</Link>
    <ChevronRight className="hidden h-3.5 w-3.5 shrink-0 text-muted-foreground/60 sm:inline" aria-hidden />
    <span className="hidden shrink-0 font-mono text-[11.5px] tabular text-muted-foreground md:inline">{q.quote_number}</span>
    <CompanyMark kind="customer" id={q.customer_id} name={q.customer_name} size="xs" className="ml-0.5" />
    <span className="min-w-0 truncate font-medium">{q.title}</span>
    <span className="hidden sm:inline-flex"><StatusBadge status={q.status} entity="quote" variant="pill" /></span>
  </div>
);

const SendForm = ({ busy, onSend, onPdf }) => {
  const [note, setNote] = useState("");
  return (
    <div className="mt-4 space-y-3">
      <Button variant="outline" className="w-full" onClick={onPdf} data-testid="quote-send-download-pdf-button"><FileDown /> Last ned PDF først</Button>
      <div><Label>Notat (loggføres på henvendelsen)</Label><Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Sendt til kontaktperson på e-post 26.09…" data-testid="quote-send-note-input" /></div>
      <Button className="w-full" disabled={busy} onClick={() => onSend(note || null)} data-testid="quote-send-confirm-button">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Bekreft sendt</Button>
    </div>
  );
};

const RejectForm = ({ busy, onReject, hasLead }) => {
  const [note, setNote] = useState("");
  const [lost, setLost] = useState(false);
  return (
    <>
      <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Årsak – f.eks. pris, konkurrent, prosjekt utsatt…" data-testid="quote-reject-note-input" />
      {hasLead && <label className="flex items-center justify-between rounded-md border px-3 py-2 text-[13px]"><span>Marker henvendelsen som tapt</span><Switch checked={lost} onCheckedChange={setLost} data-testid="quote-reject-mark-lost-switch" /></label>}
      <AlertDialogFooter><AlertDialogCancel>Avbryt</AlertDialogCancel><AlertDialogAction disabled={busy || !note.trim()} onClick={(e) => { e.preventDefault(); onReject({ note, mark_lost: lost }); }} data-testid="quote-reject-confirm-button">Marker avslått</AlertDialogAction></AlertDialogFooter>
    </>
  );
};

const AcceptSheet = ({ open, onOpenChange, quote: q, onDone }) => {
  const [f, setF] = useState({ note: "", create_project: !q.project_id, project_name: q.title, start_date: "", end_date: "", manager_id: "" });
  const accept = useMutation({
    mutationFn: () => post(`/quotes/${q.id}/accept`, { ...f, manager_id: f.manager_id || null, start_date: f.start_date || null, end_date: f.end_date || null, project_name: f.project_name || null }),
    onSuccess: (res) => { toast.success(res.created_project ? `Tilbud akseptert – prosjekt ${res.created_project.project_number} opprettet` : "Tilbud akseptert"); onOpenChange(false); onDone(res); },
    onError: (e) => toastError(e),
  });
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[480px] max-w-[94vw] overflow-y-auto" data-testid="quote-accept-sheet">
        <SheetHeader><SheetTitle>Tilbud akseptert av kunde</SheetTitle><SheetDescription>Henvendelsen markeres som vunnet. Du kan opprette prosjektet direkte – kunde, adresse, kalkyle og løsningsforslag følger med.</SheetDescription></SheetHeader>
        <div className="mt-4 space-y-3">
          <div><Label>Notat</Label><Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Aksept mottatt per e-post…" data-testid="quote-accept-note-input" /></div>
          {!q.project_id && (
            <>
              <label className="flex items-center justify-between rounded-md border px-3 py-2 text-[13px]"><span className="inline-flex items-center gap-2"><FolderKanban className="h-4 w-4 text-muted-foreground" /> Opprett prosjekt nå</span><Switch checked={f.create_project} onCheckedChange={(v) => setF({ ...f, create_project: v })} data-testid="quote-accept-create-project-switch" /></label>
              {f.create_project && (
                <div className="space-y-3 rounded-md border p-3 bg-muted/30">
                  <div><Label>Prosjektnavn</Label><Input value={f.project_name} onChange={(e) => setF({ ...f, project_name: e.target.value })} data-testid="quote-accept-project-name-input" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Startdato</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="quote-accept-start-input" /></div>
                    <div><Label>Sluttdato</Label><Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} /></div>
                  </div>
                  <div><Label>Prosjektleder</Label><UserSelect value={f.manager_id} onChange={(v) => setF({ ...f, manager_id: v })} roles={["prosjektleder", "admin"]} noneLabel="Meg" testId="quote-accept-manager-select" /></div>
                  <p className="text-xs text-muted-foreground">Tomme datoer: ønsket start fra henvendelsen + varighet.</p>
                </div>
              )}
            </>
          )}
          <Button className="w-full" disabled={accept.isPending} onClick={() => accept.mutate()} data-testid="quote-accept-confirm-button">{accept.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Bekreft aksept</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
